import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronDown, ArrowLeft, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { Eyebrow } from "@/components/common";

export default function Work() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    setData(null);
    api.get(`/works/${slug}`).then((r) => {
      setData(r.data);
      setOpenGroups({ [r.data.groups[0]?.key]: true });
    }).catch(() => {});
  }, [slug]);

  if (!data) return <div className="pt-40 text-center text-slate-400" data-testid="work-loading">Loading the work…</div>;
  const { work, groups } = data;
  const firstDoc = groups[0]?.items[0]?.id;

  return (
    <div className="pt-16 lg:pt-[72px]" data-testid="work-page">
      <section className="relative py-16 lg:py-24 border-b border-indic-gold/15 bg-[#080c1a]">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Link to="/library" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-indic-gold" data-testid="back-to-library"><ArrowLeft size={15} /> Library</Link>
          <Eyebrow className="mt-6 block">{work.kind}</Eyebrow>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl font-semibold text-amber-50">{work.title}</h1>
          <p className="mt-2 font-serif italic text-xl text-indic-gold/80">{work.subtitle}</p>
          <p className="mt-6 text-lg text-slate-300/90 leading-relaxed">{work.description}</p>
          <p className="mt-4 text-xs font-sans uppercase tracking-[0.15em] text-slate-500">Source: {work.source}</p>
          {firstDoc && (
            <Link to={`/read/${firstDoc}`} className="mt-8 inline-flex items-center gap-2 rounded-sm bg-indic-gold px-6 py-3 font-sans font-semibold text-indic-indigo hover:bg-indic-gold-soft transition-colors" data-testid="start-reading-btn">
              Start reading →
            </Link>
          )}
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <h2 className="font-serif text-3xl text-amber-50 mb-8">Contents</h2>
          <div className="space-y-3">
            {groups.map((g) => {
              const open = !!openGroups[g.key];
              return (
                <div key={g.key} className="rounded-sm border border-indic-card-border bg-indic-card overflow-hidden" data-testid={`group-${g.key}`}>
                  <button onClick={() => setOpenGroups((s) => ({ ...s, [g.key]: !s[g.key] }))}
                    className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-white/[0.02]" data-testid={`group-toggle-${g.key}`}>
                    <span className="font-serif text-xl text-amber-50">{g.title}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">{g.items.length}</span>
                      <ChevronDown size={18} className={`text-indic-gold transition-transform ${open ? "rotate-180" : ""}`} />
                    </span>
                  </button>
                  {open && (
                    <ul className="border-t border-indic-card-border divide-y divide-indic-card-border/60">
                      {g.items.map((it) => (
                        <li key={it.id}>
                          <Link to={`/read/${it.id}`} className="flex items-center gap-3 px-5 py-3 text-slate-300 hover:text-indic-gold hover:bg-white/[0.02] transition-colors group" data-testid={`doc-link-${it.id}`}>
                            <FileText size={15} className="text-slate-600 group-hover:text-indic-gold shrink-0" />
                            {it.code && <span className="text-xs font-mono text-indic-sandstone/70 shrink-0">{it.code}</span>}
                            <span className="flex-1 text-[15px]">{it.title}</span>
                            <span className="text-xs text-slate-600 shrink-0">{it.word_count}w</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
